import { useState, useEffect } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, X, Loader2, ArrowLeft, Images } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to normalize image URLs - handles both old /uploads/ and new /api/cms/images/ formats
const getImageUrl = (url) => {
  if (!url) return null;
  // If it's already a full URL, return as is
  if (url.startsWith('http')) return url;
  // If it's an API image path, prepend backend URL
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  // If it's an uploads path, try direct URL first
  if (url.startsWith('/uploads/')) return url;
  // Default: return as is
  return url;
};

const PortfolioPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [currentCategory, setCurrentCategory] = useState("all");
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [albumImages, setAlbumImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlbums();
  }, []);

  const fetchAlbums = async () => {
    try {
      const response = await axios.get(`${API}/cms/albums/public`);
      setAlbums(response.data);
    } catch (error) {
      console.error("Error fetching albums:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlbumImages = async (albumId) => {
    try {
      const response = await axios.get(`${API}/cms/albums/${albumId}`);
      setAlbumImages(response.data.images || []);
    } catch (error) {
      console.error("Error fetching album images:", error);
    }
  };

  const handleAlbumClick = (album) => {
    setSelectedAlbum(album);
    fetchAlbumImages(album.id);
  };

  const handleBackToAlbums = () => {
    setSelectedAlbum(null);
    setAlbumImages([]);
  };

  const categories = [
    { id: "all", label: "All Work" },
    { id: "bridal", label: "Bridal" },
    { id: "event", label: "Events" },
    { id: "production", label: "Production" }
  ];

  const filteredAlbums = currentCategory === "all" 
    ? albums 
    : albums.filter(album => album.category === currentCategory);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setSelectedImageIndex(index);
  };

  const navigateLightbox = (direction) => {
    const newIndex = selectedImageIndex + direction;
    if (newIndex >= 0 && newIndex < albumImages.length) {
      setSelectedImageIndex(newIndex);
      setSelectedImage(albumImages[newIndex]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="portfolio-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {!selectedAlbum ? (
          // Album Gallery View
          <>
            {/* Header */}
            <div className="text-center mb-16">
              <h1 className="text-5xl sm:text-6xl font-bold mb-6" data-testid="portfolio-heading">
                Our Work
              </h1>
              <p className="text-lg text-[#8C8C8C] max-w-2xl mx-auto">
                A showcase of our artistry across bridal, events, and production environments. 
                Every look is crafted with precision and care.
              </p>
            </div>

            {/* Category Filter */}
            <div className="flex justify-center gap-4 mb-12 flex-wrap" data-testid="category-filter">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setCurrentCategory(category.id)}
                  data-testid={`category-${category.id}`}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                    currentCategory === category.id
                      ? "bg-[#D4AF37] text-white shadow-lg"
                      : "bg-white text-[#1A1A1A] border border-[#F2EBE5] hover:border-[#D4AF37]"
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>

            {/* Albums Grid */}
            {filteredAlbums.length === 0 ? (
              <div className="text-center py-16">
                <Images className="w-16 h-16 mx-auto text-[#D4AF37]/50 mb-4" />
                <p className="text-[#8C8C8C]">No galleries available yet.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="portfolio-grid">
                {filteredAlbums.map(album => (
                  <div
                    key={album.id}
                    data-testid={`album-${album.id}`}
                    className="group cursor-pointer"
                    onClick={() => handleAlbumClick(album)}
                  >
                    <div className="relative overflow-hidden rounded-xl mb-4 aspect-[4/3]">
                      {album.cover_image ? (
                        <img
                          src={getImageUrl(album.cover_image)}
                          alt={album.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          onError={(e) => {
                            // If image fails, try API endpoint as fallback
                            const filename = album.cover_image?.split('/').pop()?.split('.')[0];
                            if (filename && !e.target.src.includes('/api/cms/images/')) {
                              e.target.src = `${BACKEND_URL}/api/cms/images/${filename}`;
                            } else {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<div class="w-full h-full bg-[#F9F8F6] flex items-center justify-center"><svg class="w-16 h-16 text-[#D4AF37]/30" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                            }
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-[#F9F8F6] flex items-center justify-center">
                          <Images className="w-16 h-16 text-[#D4AF37]/30" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-[#1A1A1A]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                        <div className="text-white">
                          <p className="text-sm font-medium opacity-80">View Gallery</p>
                        </div>
                      </div>
                      {/* Photo Count Badge */}
                      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-[#1A1A1A]">
                        {album.image_count || 0} photos
                      </div>
                    </div>
                    <h3 className="text-xl font-bold mb-1">{album.name}</h3>
                    <p className="text-sm text-[#8C8C8C] capitalize">{album.category}</p>
                    {album.description && (
                      <p className="text-sm text-[#8C8C8C] mt-2 line-clamp-2">{album.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          // Album Detail View - Show Images
          <>
            {/* Back Button and Album Header */}
            <div className="mb-8">
              <button
                onClick={handleBackToAlbums}
                className="flex items-center gap-2 text-[#8C8C8C] hover:text-[#D4AF37] transition-colors mb-6"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Galleries
              </button>
              <h1 className="text-4xl sm:text-5xl font-bold mb-4">{selectedAlbum.name}</h1>
              {selectedAlbum.description && (
                <p className="text-lg text-[#8C8C8C] max-w-2xl">{selectedAlbum.description}</p>
              )}
              <p className="text-sm text-[#D4AF37] font-medium mt-2">{albumImages.length} photos</p>
            </div>

            {/* Images Grid */}
            {albumImages.length === 0 ? (
              <div className="text-center py-16">
                <Images className="w-16 h-16 mx-auto text-[#D4AF37]/50 mb-4" />
                <p className="text-[#8C8C8C]">No photos in this gallery yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {albumImages.map((image, index) => (
                  <div
                    key={image.id}
                    className="group cursor-pointer"
                    onClick={() => openLightbox(image, index)}
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-square">
                      <img
                        src={getImageUrl(image.url)}
                        alt={image.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => {
                          const filename = image.url?.split('/').pop()?.split('.')[0];
                          if (filename && !e.target.src.includes('/api/cms/images/')) {
                            e.target.src = `${BACKEND_URL}/api/cms/images/${filename}`;
                          }
                        }}
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Lightbox Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="lightbox-modal"
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white hover:text-[#D4AF37] transition-colors z-10"
              data-testid="close-lightbox"
            >
              <X className="w-8 h-8" />
            </button>
            
            {/* Navigation Arrows */}
            {selectedImageIndex > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-[#D4AF37] transition-colors z-10 p-2 bg-black/50 rounded-full"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
            )}
            {selectedImageIndex < albumImages.length - 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-[#D4AF37] transition-colors z-10 p-2 bg-black/50 rounded-full"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            )}
            
            <div className="max-w-5xl w-full" onClick={e => e.stopPropagation()}>
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              />
              <div className="text-white text-center mt-6">
                <p className="text-sm text-white/60">
                  {selectedImageIndex + 1} of {albumImages.length}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 text-center bg-[#F2EBE5] p-12 rounded-lg">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Create Your Look?
          </h2>
          <p className="text-lg text-[#8C8C8C] mb-8">
            Let&apos;s discuss your vision and bring it to life with the same precision and artistry.
          </p>
          <a
            href="/inquiry"
            data-testid="portfolio-cta-button"
            className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#b89530] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
          >
            Book Your Consultation
          </a>
        </div>
      </div>
    </div>
  );
};

export default PortfolioPage;